// AGC: Reusable card wrapper for every chart/metric on the analytics dashboard.
// AGC: Shows title, content, optional plain-language insight (colored by severity),
// AGC: and a collapsible "?" help section with metric explanation + good/bad signals + next action.
import type { ReactNode } from 'react'
import { HelpCircle, CheckCircle2, AlertTriangle, XCircle, MinusCircle } from 'lucide-react'
import { Card } from '../../shared/Card'
import type { Insight } from '../../../services/analyticsInsightGenerator'

export interface InsightCardProps {
  title: string
  children: ReactNode
  insight?: Insight
  helpTitle?: string
  helpDescription?: string
  helpGoodSignal?: string
  helpBadSignal?: string
  helpAction?: string
  className?: string
}

// AGC: Map each severity level to its Tailwind color class and icon component
const severityStyles: Record<Insight['severity'], { text: string; Icon: typeof CheckCircle2 }> = {
  good:    { text: 'text-emerald-600 dark:text-emerald-400', Icon: CheckCircle2 },
  neutral: { text: 'text-slate-600 dark:text-slate-300',       Icon: MinusCircle  },
  warn:    { text: 'text-amber-600 dark:text-amber-400',     Icon: AlertTriangle },
  bad:     { text: 'text-red-600 dark:text-red-400',         Icon: XCircle      },
}

export function InsightCard({
  title,
  children,
  insight,
  helpTitle,
  helpDescription,
  helpGoodSignal,
  helpBadSignal,
  helpAction,
  className,
}: InsightCardProps): JSX.Element {
  // AGC: Only show the "?" button when at least one help field is present
  const hasHelp = Boolean(helpDescription || helpGoodSignal || helpBadSignal || helpAction)

  return (
    <Card className={className} padding="md">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 uppercase tracking-wide">
          {title}
        </h3>
        {hasHelp && (
          // AGC: details/summary is a native collapsible — no JS state needed
          <details className="relative">
            <summary className="cursor-pointer list-none" aria-label="Show help">
              <HelpCircle className="w-5 h-5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300" />
            </summary>
            <div className="absolute right-0 top-7 z-10 w-72 bg-white dark:bg-admin-card-dark border border-slate-200 dark:border-white/5 rounded-lg shadow-card dark:shadow-none p-4 text-sm space-y-2">
              {helpTitle && (
                <div className="font-semibold text-slate-900 dark:text-slate-100">{helpTitle}</div>
              )}
              {helpDescription && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">
                    What this measures
                  </div>
                  <div className="text-slate-700 dark:text-slate-300">{helpDescription}</div>
                </div>
              )}
              {helpGoodSignal && (
                <div>
                  <div className="text-xs font-semibold text-emerald-600 uppercase mb-1">
                    Good signal
                  </div>
                  <div className="text-slate-700 dark:text-slate-300">{helpGoodSignal}</div>
                </div>
              )}
              {helpBadSignal && (
                <div>
                  <div className="text-xs font-semibold text-red-600 uppercase mb-1">
                    Bad signal
                  </div>
                  <div className="text-slate-700 dark:text-slate-300">{helpBadSignal}</div>
                </div>
              )}
              {helpAction && (
                <div>
                  <div className="text-xs font-semibold text-blue-600 uppercase mb-1">
                    What to do
                  </div>
                  <div className="text-slate-700 dark:text-slate-300">{helpAction}</div>
                </div>
              )}
            </div>
          </details>
        )}
      </div>

      <div>{children}</div>

      {insight && (
        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-white/5">
          <div className={`flex items-start gap-2 text-sm ${severityStyles[insight.severity].text}`}>
            {(() => {
              const Icon = severityStyles[insight.severity].Icon
              return <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
            })()}
            <span>{insight.text}</span>
          </div>
        </div>
      )}
    </Card>
  )
}
